import { useState, useCallback, useRef, useEffect } from 'react';
import { dataFetch } from '../api';

export const useChartData = (chartId) => {
  const [chart, setChart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const saveTimeout = useRef(null);
  const pendingUpdates = useRef(new Map());

  const userId = localStorage.getItem('user_id');

  useEffect(() => {
    if (!chartId || !userId) {
      setError('ユーザーまたはチャートIDが見つかりません');
      setLoading(false);
      return;
    }

    const loadChart = async () => {
      setLoading(true);
      setError(null);
      try {
        let chartData = null;
        // 1. チャート本体の取得 (ID指定で取得を試みる)
        const chartRes = await dataFetch(`read/mandalacharts/${chartId}?owner_id=${userId}`);
        
        if (chartRes?.status === 'success' && chartRes?.data) {
          chartData = chartRes.data;
        } else {
          // フォールバック: クエリパラメータでの検索
          const fallbackRes = await dataFetch(`read/mandalacharts?id=${chartId}&owner_id=${userId}`);
          if (fallbackRes?.status === 'success' && fallbackRes?.data?.length > 0) {
            chartData = fallbackRes.data[0];
          }
        }

        if (!chartData) {
          throw new Error('マンダラチャートが見つかりません。削除されたか、アクセス権がありません。');
        }
        
        // 2. ブロックの取得
        const blocksRes = await dataFetch(`read/blocks?mandalachart_id=${chartData.id}&owner_id=${userId}`);
        const blocks = blocksRes?.data ?? [];
        
        if (blocks.length > 0) {
          const blockIds = blocks.map(b => b.id);
          // 3. セルの取得
          const cellsRes = await dataFetch(`read/cells?block_id[in]=${blockIds.join(',')}&owner_id=${userId}&limit=100`);
          const cells = cellsRes?.data ?? [];

          cells.forEach(c => {
            c.is_center = !!c.is_center;
          });

          const fullChart = {
            ...chartData,
            blocks: blocks.map(b => ({
              ...b,
              cells: cells.filter(c => c.block_id === b.id)
            }))
          };
          setChart(fullChart);
        } else {
           throw new Error('チャートのブロックデータが破損しています。');
        }
      } catch (err) {
        console.error(err);
        setError(err.message || 'データの読み込みに失敗しました');
      } finally {
        setLoading(false);
      }
    };
    
    loadChart();
  }, [chartId, userId]);

  const triggerSave = useCallback(() => {
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(async () => {
      if (pendingUpdates.current.size === 0) return;
      
      const recordsToUpdate = Array.from(pendingUpdates.current.values()).map(c => ({
        id: c.id,
        block_id: c.block_id,
        content: c.content,
        is_center: c.is_center ? 1 : 0,
        priority_index: c.priority_index,
        user_id: userId
      }));
      
      pendingUpdates.current.clear();
      
      if (recordsToUpdate.length > 0) {
         await dataFetch(`bulk/update/cells?owner_id=${userId}`, {
           method: 'PUT',
           body: JSON.stringify({ records: recordsToUpdate })
         });
      }
    }, 1000);
  }, [userId]);

  const updateCell = useCallback((blockId, cellId, newContent) => {
    setChart(prevChart => {
      if (!prevChart) return prevChart;
      const newChart = JSON.parse(JSON.stringify(prevChart)); 
      
      let sourceBlock = null;
      let sourceCell = null;

      for (const block of newChart.blocks) {
        if (block.id === blockId) {
          const cell = block.cells.find(c => c.id === cellId);
          if (cell) {
            cell.content = newContent;
            sourceBlock = block;
            sourceCell = cell;
            pendingUpdates.current.set(cell.id, cell);
          }
          break;
        }
      }

      if (!sourceBlock || !sourceCell) return newChart;

      if (sourceBlock.block_type === 'CORE' && !sourceCell.is_center) {
        const targetBlockType = `BLOCK_${sourceCell.priority_index}`;
        const targetBlock = newChart.blocks.find(b => b.block_type === targetBlockType);
        if (targetBlock) {
          const targetCenter = targetBlock.cells.find(c => c.is_center);
          if (targetCenter) {
            targetCenter.content = newContent;
            pendingUpdates.current.set(targetCenter.id, targetCenter);
          }
        }
      } else if (sourceBlock.block_type !== 'CORE' && sourceCell.is_center) {
        const priorityIndex = parseInt(sourceBlock.block_type.replace('BLOCK_', ''), 10);
        const coreBlock = newChart.blocks.find(b => b.block_type === 'CORE');
        if (coreBlock) {
          const targetCoreCell = coreBlock.cells.find(c => c.priority_index === priorityIndex);
          if (targetCoreCell) {
            targetCoreCell.content = newContent;
            pendingUpdates.current.set(targetCoreCell.id, targetCoreCell);
          }
        }
      }

      triggerSave();
      return newChart;
    });
  }, [triggerSave]);

  const updateCellPriority = useCallback((blockId, cellId, newPriority) => {
    setChart(prevChart => {
      const newChart = JSON.parse(JSON.stringify(prevChart));
      const block = newChart.blocks.find(b => b.id === blockId);
      if (!block) return newChart;

      const targetCell = block.cells.find(c => c.id === cellId);
      const cellToSwap = block.cells.find(c => c.priority_index === newPriority && !c.is_center);

      if (targetCell && cellToSwap && targetCell.id !== cellToSwap.id) {
        const tempPriority = targetCell.priority_index;
        targetCell.priority_index = newPriority;
        cellToSwap.priority_index = tempPriority;
        
        pendingUpdates.current.set(targetCell.id, targetCell);
        pendingUpdates.current.set(cellToSwap.id, cellToSwap);
      }
      
      triggerSave();
      return newChart;
    });
  }, [triggerSave]);

  const bulkUpdateChart = useCallback(async (newChartData) => {
    setChart(newChartData);
    const records = [];
    newChartData.blocks.forEach(b => {
      b.cells.forEach(c => {
        records.push({
          id: c.id,
          block_id: c.block_id,
          content: c.content,
          is_center: c.is_center ? 1 : 0,
          priority_index: c.priority_index,
          user_id: userId
        });
      });
    });
    
    await dataFetch(`bulk/update/cells?owner_id=${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ records })
    });
  }, [userId]);

  return { chart, setChart, loading, error, updateCell, updateCellPriority, bulkUpdateChart };
};